<script lang="ts" setup>
// @ts-expect-error virtual import
import type { NuxtError } from '#app'
// @ts-expect-error virtual import
import { clearError, clearNuxtData, clearNuxtState } from '#imports'
import { computed, nextTick, ref } from 'vue'
import { useLocale } from '../../composables/locale'
import { Button } from '../ui/button'

const props = defineProps({
  error: Object as () => NuxtError,
})

const { t } = useLocale()
const processing = ref(false)

const defaultTitles: { [key: number]: string } = {
  400: t('error.400.heading'),
  401: t('error.401.heading'),
  403: t('error.403.heading'),
  404: t('error.404.heading'),
  405: t('error.405.heading'),
  406: t('error.406.heading'),
  409: t('error.409.heading'),
  410: t('error.410.heading'),
  413: t('error.413.heading'),
  414: t('error.414.heading'),
  415: t('error.415.heading'),
  500: t('error.500.heading'),
  501: t('error.501.heading'),
  502: t('error.502.heading'),
  503: t('error.503.heading'),
  504: t('error.504.heading'),
  505: t('error.505.heading'),
}

const defaultMessages: { [key: number]: string } = {
  400: t('error.400.description'),
  401: t('error.401.description'),
  403: t('error.403.description'),
  404: t('error.404.description'),
  405: t('error.405.description'),
  406: t('error.406.description'),
  409: t('error.409.description'),
  410: t('error.410.description'),
  413: t('error.413.description'),
  414: t('error.414.description'),
  415: t('error.415.description'),
  500: t('error.500.description'),
  501: t('error.501.description'),
  502: t('error.502.description'),
  503: t('error.503.description'),
  504: t('error.504.description'),
  505: t('error.505.description'),
}

const networkErrorMessages = [
  'network error',
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'The Internet connection appears to be offline.',
  'Load failed',
  'Network request failed',
  'fetch failed',
  'terminated',
]

const isNetworkError = computed(() => {
  const _error = props.error
  if (_error instanceof Error) {
    for (const networkErrorMessage of networkErrorMessages) {
      if (_error.message.toLocaleLowerCase().includes(networkErrorMessage.toLocaleLowerCase())) {
        return true
      }
    }
  }
  return false
})

const statusCode = computed(() => {
  if (isNetworkError.value) {
    return 502
  }
  return props.error?.status || 500
})

const errorTitle = computed(() => {
  if (isNetworkError.value) {
    return t('error.network.heading')
  }
  return defaultTitles[statusCode.value] || 'Error'
})

const errorMessage = computed(() => {
  const _error = props.error
  if (isNetworkError.value) {
    return t('error.network.description')
  }
  return defaultMessages[statusCode.value] || _error?.statusMessage || 'Error'
})

async function onHomePage() {
  processing.value = true
  await nextTick()

  clearNuxtState(undefined, {
    reset: false,
  })
  clearNuxtData()

  await clearError({
    redirect: '/',
  })
}
</script>

<template>
  <section class="py-16 lg:py-24">
    <div class="container px-6 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 relative z-10 mx-auto">
      <div class="flex flex-col gap-6 lg:gap-8 flex-1 max-w-xl items-center text-center m-auto">
        <div class="flex flex-col gap-4 lg:gap-5">
          <p
            class="text-muted-foreground text-sm lg:text-base font-semibold"
            aria-label="Error code"
          >
            {{ statusCode }}
          </p>
          <h1
            id="error-title"
            class="text-foreground title-3xl md:title-5xl"
          >
            {{ errorTitle }}
          </h1>
          <p class="text-muted-foreground text-base lg:text-lg">
            {{ errorMessage }}
          </p>
        </div>

        <Button
          :loading="processing"
          @click="onHomePage"
        >
          {{ t('goToHomepage.btn') }}
        </Button>
      </div>
    </div>
  </section>
</template>
